/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useUserSearch.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing user search modal in tenant form.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect } from 'react';
import { useAuthStore } from '../../../store/auth';
import { buildApiUrl, getApiHeaders, API_CONFIG } from '../../../config/api';

interface User {
  id?: string;
  username: string;
  email: string;
  fullName?: string;
  department?: string;
  locale: string;
  timezone: string;
  startupDialog: string;
  totpEnabled: boolean;
  isActive: boolean;
  profileId?: string;
  logProfileId?: string;
  tenantIds?: string[];
}

export function useUserSearch() {
  const { user } = useAuthStore();
  const tenantSlug = user?.tenant?.slug || '';

  const [showUserList, setShowUserList] = useState(false);
  const [userListSearch, setUserListSearch] = useState('');
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);

  const loadAvailableUsers = async () => {
    try {
      const response = await fetch(
        buildApiUrl(`${API_CONFIG.ENDPOINTS.USERS}?search=${encodeURIComponent(userListSearch)}`),
        {
          headers: getApiHeaders(tenantSlug, user?.id)
        }
      );

      if (response.ok) {
        const result = await response.json();
        setAvailableUsers(result.data || []);
      } else {
        setAvailableUsers([]);
      }
    } catch (error) {
      setAvailableUsers([]);
    }
  };

  // Load users when user list modal opens
  useEffect(() => {
    if (showUserList) {
      loadAvailableUsers();
    }
  }, [showUserList, userListSearch]);

  return {
    showUserList,
    setShowUserList,
    userListSearch,
    setUserListSearch,
    availableUsers
  };
}

